import { useState, useMemo } from 'react';
import { ArrowDownToLine, ArrowUpFromLine, SlidersHorizontal, Download, History } from 'lucide-react';
import { Card, CardContent, ErrorState } from '@mustauto/ui/web';
import { EmptyState } from '@mustauto/ui/web';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { formatStockNumber } from '@mustauto/utils';

interface Movement {
  id: string;
  itemId: string;
  itemName: string;
  type: 'in' | 'out' | 'adjustment';
  quantity: number;
  notes: string | null;
  osId: string | null;
  purchaseOrderId: string | null;
  createdAt: string;
}

function useMovements(filters: { type?: string; itemName?: string; from?: string; to?: string }) {
  const params = new URLSearchParams();
  if (filters.type) params.set('type', filters.type);
  if (filters.itemName) params.set('itemName', filters.itemName);
  if (filters.from) params.set('from', filters.from);
  if (filters.to) params.set('to', filters.to);
  const qs = params.toString();

  return useQuery<{ movements: Movement[] }>({
    queryKey: ['inventory-movements', filters],
    queryFn: async () => {
      const res = await api.get(`/inventory/movements${qs ? `?${qs}` : ''}`);
      return res.data;
    },
  });
}

const typeConfig: Record<string, { icon: typeof ArrowDownToLine; className: string; label: string }> = {
  in: { icon: ArrowDownToLine, className: 'text-emerald-600 bg-emerald-50 dark:text-emerald-300 dark:bg-emerald-950/40', label: 'Entrada' },
  out: { icon: ArrowUpFromLine, className: 'text-amber-600 bg-amber-50 dark:text-amber-300 dark:bg-amber-950/40', label: 'Saída' },
  adjustment: { icon: SlidersHorizontal, className: 'text-blue-600 bg-blue-50 dark:text-blue-300 dark:bg-blue-950/40', label: 'Ajuste' },
};

export function MovementsTab() {
  const [typeFilter, setTypeFilter] = useState('');
  const [itemFilter, setItemFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const { data, isLoading, isError, error, refetch } = useMovements({
    type: typeFilter || undefined,
    itemName: itemFilter || undefined,
    from: fromDate || undefined,
    to: toDate || undefined,
  });

  const movements = data?.movements ?? [];

  // Group by date
  const grouped = useMemo(() => {
    const groups = new Map<string, Movement[]>();
    for (const m of movements) {
      const date = new Date(m.createdAt).toLocaleDateString('pt-BR');
      const existing = groups.get(date);
      if (existing) {
        existing.push(m);
      } else {
        groups.set(date, [m]);
      }
    }
    return Array.from(groups.entries());
  }, [movements]);

  const handleExportCSV = () => {
    if (movements.length === 0) return;
    const headers = ['Data', 'Item', 'Tipo', 'Quantidade', 'Observação'];
    const rows = movements.map((m) => [
      new Date(m.createdAt).toLocaleString('pt-BR'),
      m.itemName,
      typeConfig[m.type]?.label ?? m.type,
      String(m.quantity),
      m.notes ?? '',
    ]);
    const csv = [headers, ...rows].map((r) => r.map((c) => `"${c}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `movimentacoes_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <Card>
        <CardContent className="pt-4 pb-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-muted-foreground">Tipo</label>
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="rounded-md border border-input bg-background px-3 py-1.5 text-sm"
              >
                <option value="">Todos</option>
                <option value="in">Entrada</option>
                <option value="out">Saída</option>
                <option value="adjustment">Ajuste</option>
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-muted-foreground">Item</label>
              <input
                type="text"
                value={itemFilter}
                onChange={(e) => setItemFilter(e.target.value)}
                placeholder="Nome do item"
                className="rounded-md border border-input bg-background px-3 py-1.5 text-sm w-48"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-muted-foreground">De</label>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="rounded-md border border-input bg-background px-3 py-1.5 text-sm"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-muted-foreground">Até</label>
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="rounded-md border border-input bg-background px-3 py-1.5 text-sm"
              />
            </div>
            {(typeFilter || itemFilter || fromDate || toDate) && (
              <button
                onClick={() => { setTypeFilter(''); setItemFilter(''); setFromDate(''); setToDate(''); }}
                className="rounded-md px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                Limpar
              </button>
            )}
            <div className="ml-auto">
              <button
                onClick={handleExportCSV}
                disabled={movements.length === 0}
                className="flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
              >
                <Download size={14} />
                Exportar CSV
              </button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Timeline */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        </div>
      ) : isError && grouped.length === 0 ? (
        <ErrorState title="Não foi possível carregar as movimentações" error={error} onRetry={() => refetch()} />
      ) : grouped.length === 0 ? (
        <EmptyState
          icon={<History size={32} />}
          title="Nenhuma movimentação registrada"
          description="Movimentações aparecem aqui quando você recebe peças (OC), consome em OS, faz ajustes manuais ou registra baixas. Não criáveis diretamente nesta tela."
        />
      ) : (
        <div className="space-y-6">
          {grouped.map(([date, dayMovements]) => (
            <div key={date}>
              <div className="sticky top-0 z-10 bg-background/95 backdrop-blur py-1 mb-2">
                <h3 className="text-sm font-semibold text-muted-foreground">{date}</h3>
              </div>
              <div className="space-y-2">
                {dayMovements.map((m) => {
                  const config = typeConfig[m.type] ?? typeConfig.adjustment;
                  const Icon = config.icon;
                  return (
                    <Card key={m.id}>
                      <CardContent className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          <div className={`rounded-full p-2 ${config.className}`}>
                            <Icon size={16} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-sm">{m.itemName}</span>
                              <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${config.className}`}>
                                {config.label}
                              </span>
                            </div>
                            <div className="text-xs text-muted-foreground mt-0.5">
                              {m.type === 'in' ? '+' : m.type === 'out' ? '-' : m.type === 'adjustment' ? '= ' : ''}{formatStockNumber(m.quantity)}
                              {m.notes && <> &middot; {m.notes}</>}
                            </div>
                          </div>
                          <div className="text-xs text-muted-foreground shrink-0">
                            {new Date(m.createdAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
